import { TemplateContext, ReportTemplate } from '../../models/reportTemplate.model';
import { CustomField } from '../../models/customField.model';
import { IGridService } from '../../shared/grid/grid.service';
import { FieldSourceKind } from './field-source-kind.enum';

export interface TemplateMapperConfig {
    entityType: string;
    supportedFilters: string[];
}

export interface RuntimeEntityGroupConfig {
    key: string;
    label: string;
    accent: string;
    fields: readonly string[];
}

export interface FilterOption {
    path: string;
    label: string;
    availableValues: FilterValue[];
    selectedValues: (number | string)[];
}

export interface FilterValue {
    value: number | string;
    label: string;
    count: number;
    disabled?: boolean;
}

export interface EmailTemplate {
    subject: string;
    message: string;
    recipientId?: string;
    recipientEmail?: string;
    ccEmails?: string[];
}

export interface InlineFieldDescriptor {
    path: string;
    label: string;
    isImage?: boolean;
}

export type EntityFieldSource =
    | { key: string; label: string; accent: string; kind: FieldSourceKind.BeGrid; service: IGridService<unknown>; pathPrefix: string; overlay?: InlineFieldDescriptor[]; }
    | { key: string; label: string; accent: string; kind: FieldSourceKind.Inline; fields: InlineFieldDescriptor[]; };

export { FieldSourceKind };

export abstract class BaseTemplateMapper<T = unknown> {
    abstract readonly config: TemplateMapperConfig;

    abstract mapToTemplateContext(
        entity: T,
        template: ReportTemplate,
        userInputs: Record<string, string | boolean>,
        filteredData?: unknown[]
    ): Promise<TemplateContext>;

    abstract getDefaultUserInputs(): Record<string, string>;

    abstract getEmailTemplate(entity: T, customFields?: CustomField[]): EmailTemplate;

    abstract analyzeFilters(entity: T, template?: ReportTemplate): FilterOption[];

    abstract applyFilters(entity: T, filters: Record<string, (number | string)[]>): unknown[];

    abstract getEntityFieldSources(): EntityFieldSource[];

    getSyntheticFields(): InlineFieldDescriptor[] {
        return [
            { path: 'reportGeneratedBy', label: 'Report Generated By' },
            { path: 'generatedAt', label: 'Generated At' },
            { path: 'copyrightYear', label: 'Copyright Year' },
            { path: 'reportGeneratorTitle', label: 'Report Generator Title' },
        ];
    }

    getEntityPreviewCss(scope: string): string { return ''; }

    updateCascadingFilters(entity: T, filters: FilterOption[], changedFilterPath: string): void {}

    async loadAdditionalData(entity: T): Promise<void> {}

    protected extractFilterPath(path: string): string {
        const last = path.includes('.') ? path.split('.').pop()! : path;
        return last.charAt(0).toLowerCase() + last.slice(1);
    }

    protected createDynamicProxy(sourceData: object | null | undefined, customMappings: Record<string, unknown> = {}): Record<string, unknown> {
        const target = (sourceData ?? {}) as Record<string, unknown>;
        return new Proxy(target, {
            get: (_, prop: PropertyKey, receiver) =>
                prop in customMappings ? customMappings[prop as string] : Reflect.get(target, prop, receiver),
            has: (_, prop: PropertyKey) => prop in customMappings || Reflect.has(target, prop),
            ownKeys: (target) => {
                const keys = new Set<string | symbol>([
                    ...Reflect.ownKeys(target).filter(k => typeof k === 'string' || typeof k === 'symbol') as (string | symbol)[],
                    ...Object.keys(customMappings)
                ]);
                return Array.from(keys);
            },
            getOwnPropertyDescriptor: (_, prop: PropertyKey) => {
                if (prop in customMappings) {
                    return {
                        configurable: true,
                        enumerable: true,
                        value: customMappings[prop as string],
                        writable: true
                    };
                }
                return Reflect.getOwnPropertyDescriptor(target, prop);
            }
        });
    }

    protected buildFilterValues<TItem>(
        items: TItem[],
        idExtractor: (item: TItem) => number | string | null | undefined,
        nameExtractor: (item: TItem) => string
    ): FilterValue[] {
        const filterMap = new Map<number | string, { name: string; count: number }>();

        items.forEach(item => {
            const id = idExtractor(item);
            if (id === null || id === undefined) {
                return;
            }
            const existing = filterMap.get(id);
            filterMap.set(id, existing ? { ...existing, count: existing.count + 1 } : { name: nameExtractor(item), count: 1 });
        });

        return Array.from(filterMap.entries()).map(([id, data]) => ({ value: id, label: data.name, count: data.count }));
    }

    protected isValidEmail(email: string): boolean {
        const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return EMAIL_REGEX.test(email);
    }

    protected getFieldValue(entity: Record<string, unknown>, fieldType: number, customFields?: CustomField[]): string {
        if (!customFields) return '';

        const field = customFields.find(f => f.type === fieldType);
        const value = field ? entity[field.property] : undefined;
        return typeof value === 'string' ? value : '';
    }
}
